import InputField from 'components/FormFields/InputField';
import { PayoutModalPropsType } from 'components/PayoutManagement/types';
import { addPayoutMethod, updatePayoutMethod } from 'constants/apiUrls';
import { PayoutMethodEnum } from 'enum/common';
import { useFormik } from 'formik';
import { getCountryCallingCode, isValidNumber, CountryCode } from 'libphonenumber-js';
import { useAppSelector } from 'hooks/useRedux';
import { useAuth } from 'hooks/useAuth';
import { http } from 'middleware/apiMiddleware';
import { useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import { RootState } from 'store';
import * as Yup from 'yup';
import RadioField from 'components/FormFields/RadioField';
import PhoneInputWithFlag, { CountryOption } from 'components/PayoutManagement/PhoneInputWithFlag';

interface IZelleFormProps {
  data: PayoutModalPropsType;
  afterSubmit: () => void;
  isSubmitting: boolean;
  setIsSubmitting: (value: boolean) => void;
}

enum SubmissionTypeEnum {
  EMAIL = 'email',
  PHONE = 'phoneNumber',
}

const getCallingCode = (code: string): string => {
  try {
    return `+${getCountryCallingCode(code as CountryCode)}`;
  } catch {
    return '+1';
  }
};

const ZelleForm: React.FC<IZelleFormProps> = ({ data, afterSubmit, setIsSubmitting }) => {
  const authContext = useAuth();
  const user = authContext?.user as Record<string, any> | undefined;

  const userEmail = user?.email || '';
  const userPhone = user?.phoneNumber || user?.phone_number || '';
  const userCountryCode = user?.phoneCountryCode || user?.phone_country_code || '';
  const userCountryExt = user?.phoneCountryExtension || user?.phone_country_extension || '';
  const userCountryName = user?.phoneCountryName || user?.phone_country_name || '';

  const defaultSubmissionType = (data?.data?.phoneNumber || userPhone)
    ? SubmissionTypeEnum.PHONE
    : SubmissionTypeEnum.EMAIL;
  const [submissionType, setSubmissionType] = useState<SubmissionTypeEnum | null>(
    defaultSubmissionType,
  );
  const selectedBusiness = useAppSelector((state: RootState) => state.businesses.business);

  const defaultCountryCode =
    data?.data?.phoneCountryCode || userCountryCode || selectedBusiness?.address?.countryShortCode || 'US';

  const [selectedCountryOption, setSelectedCountryOption] = useState<CountryOption | null>(null);

  const phoneCountryExtension = useMemo(() => {
    if (data?.data?.phoneCountryExtension && !selectedCountryOption)
      return data.data.phoneCountryExtension;
    if (userCountryExt && !selectedCountryOption)
      return userCountryExt;
    return selectedCountryOption?.callingCode || getCallingCode(defaultCountryCode);
  }, [data?.data?.phoneCountryExtension, userCountryExt, selectedCountryOption, defaultCountryCode]);

  const validationSchema = (submissionType: SubmissionTypeEnum | null, countryExt: string) =>
    Yup.object({
      email: Yup.string()
        .email('Invalid email format')
        .when([], {
          is: () => submissionType === SubmissionTypeEnum.EMAIL,
          then: (schema) => schema.required('Email is required'),
          otherwise: (schema) => schema.notRequired(),
        }),

      phoneNumber: Yup.string()
        .test('is-valid-phone', 'Invalid phone number', (value) => {
          if (!value) return true;
          return isValidNumber(`${countryExt}${value}`);
        })
        .when([], {
          is: () => submissionType === SubmissionTypeEnum.PHONE,
          then: (schema) => schema.required('Phone number is required'),
          otherwise: (schema) => schema.notRequired(),
        }),
    });

  const isEdit = data?.type === 'edit';

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: data?.data?.email || userEmail,
      phoneNumber: data?.data?.phoneNumber || userPhone,
      phoneCountryName: data?.data?.phoneCountryName || userCountryName || selectedBusiness?.address?.country,
      phoneCountryCode: selectedCountryOption?.code || defaultCountryCode,
      phoneCountryExtension: phoneCountryExtension,
    },
    validationSchema: validationSchema(submissionType, phoneCountryExtension),
    onSubmit: async (values) => {
      try {
        setIsSubmitting(true);
        const payload =
          submissionType === 'email'
            ? {
                email: values?.email,
              }
            : {
                phoneCountryExtension: phoneCountryExtension,
                phoneCountryName: selectedCountryOption?.name || selectedBusiness?.address?.country,
                phoneCountryCode: selectedCountryOption?.code || defaultCountryCode,
                phoneNumber: values?.phoneNumber,
                email: '',
              };
        if (isEdit) {
          await http.patch(
            updatePayoutMethod(
              PayoutMethodEnum.ZELLE,
              data?.data?.uuid || '',
              selectedBusiness?.uuid || '',
            ),
            payload,
          );
        } else {
          await http.post(
            addPayoutMethod(PayoutMethodEnum.ZELLE, selectedBusiness?.uuid || ''),
            payload,
          );
        }
        afterSubmit();
      } catch (error) {
        toast('Unexpected error occurred while adding/updating payout method');
      } finally {
        setIsSubmitting(false);
      }
    },
  });
  const { values, setFieldValue, errors, handleSubmit, touched } = formik;

  return (
    <div>
      <form onSubmit={handleSubmit} id={PayoutMethodEnum.ZELLE}>
        <RadioField
          containerClass={'flex flex-row'}
          value={submissionType || ''}
          options={[
            { label: 'Email Address', value: SubmissionTypeEnum.EMAIL },
            { label: 'Phone Number', value: SubmissionTypeEnum.PHONE },
          ]}
          name={'zelleForm'}
          onChange={(value) => {
            setSubmissionType(value?.value);
            setFieldValue('email', data?.data?.email || userEmail);
            setFieldValue('phoneNumber', data?.data?.phoneNumber || userPhone);
          }}
        />
        {submissionType === SubmissionTypeEnum.EMAIL ? (
          <InputField
            id="email"
            placeholder="Enter Email Address"
            onChange={(e) => {
              setFieldValue('email', e.target.value);
            }}
            value={values?.email}
            error={touched.email && errors.email}
          />
        ) : (
          <PhoneInputWithFlag
            defaultCountryCode={defaultCountryCode}
            initialPhoneCountryExtension={data?.data?.phoneCountryExtension}
            phoneNumber={values?.phoneNumber || ''}
            onPhoneChange={(value) => setFieldValue('phoneNumber', value)}
            onCountryChange={(option) => {
              setSelectedCountryOption(option);
              setFieldValue('phoneCountryCode', option.code);
            }}
            error={errors.phoneNumber ? String(errors.phoneNumber) : undefined}
            touched={!!touched.phoneNumber}
          />
        )}
      </form>
    </div>
  );
};

export default ZelleForm;
